import React, { useState, useMemo, useRef, useCallback } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Brush, ReferenceDot, ReferenceArea } from 'recharts';
import { toPng } from 'html-to-image';
import { Download, TrendingUp, TrendingDown, AlertCircle, Layers, Activity, BarChart2, Save, Search, FileText, Cpu, CheckSquare, ShieldCheck, ToggleLeft, ToggleRight } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

const data = [
  { month: 'Jan 25', value: 1100000, target: 1000000, benchmark: 950000, volume: 120000, anomaly: false, anomalyCount: 0 },
  { month: 'Feb 25', value: 1250000, target: 1100000, benchmark: 1050000, volume: 150000, anomaly: false, anomalyCount: 0 },
  { month: 'Mar 25', value: 1400000, target: 1250000, benchmark: 1180000, volume: 200000, anomaly: false, anomalyCount: 0 },
  { month: 'Apr 25', value: 1300000, target: 1400000, benchmark: 1300000, volume: 300000, anomaly: true, anomalyCount: 2, anomalyReason: 'Correction Period & Volatility' },
  { month: 'May 25', value: 1500000, target: 1450000, benchmark: 1400000, volume: 180000, anomaly: false, anomalyCount: 0 },
  { month: 'Jun 25', value: 1650000, target: 1550000, benchmark: 1520000, volume: 210000, anomaly: false, anomalyCount: 0 },
  { month: 'Jul 25', value: 1800000, target: 1700000, benchmark: 1650000, volume: 220000, anomaly: false, anomalyCount: 0 },
  { month: 'Aug 25', value: 1850000, target: 1800000, benchmark: 1780000, volume: 150000, anomaly: false, anomalyCount: 0 },
  { month: 'Sep 25', value: 1950000, target: 1900000, benchmark: 1910000, volume: 190000, anomaly: false, anomalyCount: 0 },
  { month: 'Oct 25', value: 2000000, target: 1950000, benchmark: 2050000, volume: 160000, anomaly: false, anomalyCount: 0 },
  { month: 'Nov 25', value: 2050000, target: 2000000, benchmark: 2150000, volume: 140000, anomaly: false, anomalyCount: 0 },
  { month: 'Dec 25', value: 2100000, target: 2050000, benchmark: 2250000, volume: 170000, anomaly: false, anomalyCount: 0 },
  { month: 'Jan 26', value: 2100000, target: 2000000, benchmark: 2360000, volume: 150000, anomaly: false, anomalyCount: 0 },
  { month: 'Feb 26', value: 2400000, target: 2150000, benchmark: 2480000, volume: 300000, anomaly: false, anomalyCount: 0 },
  { month: 'Mar 26', value: 2900000, target: 2300000, benchmark: 2600000, volume: 500000, anomaly: true, anomalyCount: 5, anomalyReason: 'Sudden Capital Injection & Multi-system Alert' },
  { month: 'Apr 26', value: 3400000, target: 2500000, benchmark: 2750000, volume: 100000, anomaly: false, anomalyCount: 0 },
  { month: 'May 26', value: 3800000, target: 2700000, benchmark: 2900000, volume: 400000, anomaly: false, anomalyCount: 0 },
  { month: 'Jun 26', value: 4200000, target: 3000000, benchmark: 3050000, volume: 400000, anomaly: false, anomalyCount: 0 },
  { month: 'Jul 26', value: 4500000, target: 3200000, benchmark: 3220000, volume: 300000, anomaly: false, anomalyCount: 0 },
  { month: 'Aug 26', value: 4700000, target: 3450000, benchmark: 3400000, volume: 200000, anomaly: false, anomalyCount: 0 },
  { month: 'Sep 26', value: 4900000, target: 3600000, benchmark: 3600000, volume: 200000, anomaly: false, anomalyCount: 0 },
  { month: 'Oct 26', value: 5000000, target: 3800000, benchmark: 3800000, volume: 100000, anomaly: true, anomalyCount: 3, anomalyReason: 'Growth Stagnation & Rate Drop' },
  { month: 'Nov 26', value: 5050000, target: 4000000, benchmark: 4020000, volume: 50000, anomaly: false, anomalyCount: 0 },
  { month: 'Dec 26', value: 5102443, target: 4200000, benchmark: 4250000, volume: 52443, anomaly: false, anomalyCount: 0 },
];

type FilterOption = 'Last 3 Months' | 'Last 6 Months' | 'Year to Date' | 'All Time';

const filterLabels: Record<FilterOption, string> = {
  'Last 3 Months': 'Last 3 Months',
  'Last 6 Months': 'Last 6 Months',
  'Year to Date': 'Year to Date',
  'All Time': 'All Time'
};

const DEFAULT_NODES = [
  {
    id: 'Node-01',
    name: 'Vanguard-Closer',
    code: '01',
    status: 'ACTIVE',
    category: '15m Trial Ingress',
    lastExecution: '2 mins ago',
    description: 'Bypasses standard administrative queues. Ingresses new leads with rapid 15-minute trial countdown triggers.',
    latency: '0.8s'
  },
  {
    id: 'Node-06',
    name: 'The Oracle',
    code: '06',
    status: 'ACTIVE',
    category: '2m Heavy-Data Extractor',
    lastExecution: 'Just now',
    description: 'Retrieves heavy corporate processing datasets, tracks Stripe liquidity benchmarks, and archives telemetry logs.',
    latency: '1.2s'
  },
  {
    id: 'Node-16',
    name: 'The Gavel',
    code: '16',
    status: 'SYNCING',
    category: '8m Contract Closure',
    lastExecution: '34 secs ago',
    description: 'Applies automated legal protocols, formats placement schedules, and triggers verification metrics.',
    latency: '0.5s'
  },
  {
    id: 'Node-17',
    name: 'Chronos Ledger',
    code: '17',
    status: 'STANDBY',
    category: 'Temporal Synchronization',
    lastExecution: '15 mins ago',
    description: 'Aligns microsecond audit timestamps with the imperial cloud ledger backup indexing cycle.',
    latency: '0.2s'
  },
  {
    id: 'Node-07',
    name: 'Quantum Billing Ledger',
    code: '07',
    status: 'ACTIVE',
    category: 'Multi-stage Revenue Ticker',
    lastExecution: '1 min ago',
    description: '1% Levy charity extraction routing + 10% Maintenance & Hosting Fee auditing. Multi-stage ledger targeting Sort Code: 04-06-05.',
    latency: '0.9s'
  },
  {
    id: 'Node-09.2',
    name: 'Antigravity Sandbox stack',
    code: '09.2',
    status: 'ACTIVE',
    category: 'Autonomous Linux sandbox',
    lastExecution: '3 mins ago',
    description: 'Orchestrates managed interactions via sandbox execution channels, syncing Google Drive NexoraX library logs.',
    latency: '0.1s'
  },
  {
    id: 'Node-18.5',
    name: 'Secretary-Node Chief of Staff',
    code: '18.5',
    status: 'SUSPENDED',
    category: 'Secure Registry Scribe',
    lastExecution: '1 hour ago',
    description: 'Coordinates compliance trackers for Pakistan Remote nodes (Staff Coordinator) and tracks candidate reviews.',
    latency: '2.5s'
  }
];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const value = payload.find((p: any) => p.dataKey === 'value')?.value || payload[0]?.payload.value;
    const target = payload.find((p: any) => p.dataKey === 'target')?.value || payload[0]?.payload.target;
    const benchmark = payload.find((p: any) => p.dataKey === 'benchmark')?.value || payload[0]?.payload.benchmark;
    const diff = target ? value - target : 0;
    const diffPercentage = target ? (diff / target) * 100 : 0;

    return (
      <div className="bg-[#050914]/95 border border-cyan-500/30 rounded-lg p-3 shadow-xl backdrop-blur-md">
        <p className="text-slate-400 font-mono text-[10px] uppercase tracking-widest mb-2 border-b border-white/5 pb-1">{label}</p>
        <div className="space-y-1.5">
          <div className="flex justify-between items-center gap-4">
            <span className="flex items-center gap-1.5 text-xs font-mono text-cyan-500">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400"></span> Actual
            </span>
            <span className="font-sans font-bold text-white tracking-wide">£{value?.toLocaleString()}</span>
          </div>
          {target && (
            <div className="flex justify-between items-center gap-4">
              <span className="flex items-center gap-1.5 text-xs font-mono text-indigo-400">
                <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 border border-transparent border-t-indigo-400 border-dashed"></span> Target
              </span>
              <span className="font-sans text-slate-300">£{target?.toLocaleString()}</span>
            </div>
          )}
          {benchmark && (
            <div className="flex justify-between items-center gap-4">
              <span className="flex items-center gap-1.5 text-xs font-mono text-amber-500">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 border border-transparent border-t-amber-500 border-dashed"></span> Benchmark
              </span>
              <span className="font-sans text-slate-300">£{benchmark?.toLocaleString()}</span>
            </div>
          )}
          {payload[0]?.payload.anomaly && (
            <div className="flex justify-between items-center gap-4 mt-2 pt-2 border-t border-rose-500/20">
              <span className="flex items-center gap-1.5 text-[10px] font-mono text-rose-400">
                <AlertCircle className="w-3 h-3" /> Anomaly Detected
              </span>
              <span className="font-sans text-[10px] text-slate-400">{payload[0].payload.anomalyReason}</span>
            </div>
          )}
        </div>
        {target && (
          <div className={`mt-2 pt-2 border-t border-white/5 text-[10px] font-mono uppercase flex items-center justify-end gap-1 ${diff >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
            {diff >= 0 ? '+' : ''}{diffPercentage.toFixed(1)}% vs Target
          </div>
        )}
      </div>
    );

  }
  return null;
};

export const VaultChart: React.FC = () => {
  const [filter, setFilter] = useState<FilterOption>('Year to Date');
  const [showActual, setShowActual] = useState(true);
  const [showTarget, setShowTarget] = useState(true);
  const [showBenchmark, setShowBenchmark] = useState(false);
  const [isCumulative, setIsCumulative] = useState(true);
  const [startDate, setStartDate] = useState('2026-01-01');
  const [endDate, setEndDate] = useState('2026-12-31');
  const chartRef = useRef<HTMLDivElement>(null);

  // Vault automation nodes persistent configuration state
  const [nodes, setNodes] = useState(() => {
    const saved = localStorage.getItem('vault_automation_nodes');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error("Failed to parse saved nodes:", e);
      }
    }
    return DEFAULT_NODES;
  });

  // Search filter query state
  const [searchQuery, setSearchQuery] = useState('');

  // Save current nodes configuration as snapshot
  const handleSaveSnapshot = useCallback(() => {
    localStorage.setItem('vault_automation_nodes', JSON.stringify(nodes));
    alert('SUCCESS: Vault State Snapshot saved to local storage! All active node configurations are synchronized and persistent across session reloads.');
  }, [nodes]);

  // Toggle status of specific node
  const handleToggleNodeStatus = useCallback((nodeId: string) => {
    setNodes(prev => prev.map(node => {
      if (node.id === nodeId) {
        const statuses: Array<'ACTIVE' | 'STANDBY' | 'SYNCING' | 'SUSPENDED'> = ['ACTIVE', 'STANDBY', 'SYNCING', 'SUSPENDED'];
        const currentIdx = statuses.indexOf(node.status as any);
        const nextIdx = (currentIdx + 1) % statuses.length;
        return { ...node, status: statuses[nextIdx] };
      }
      return node;
    }));
  }, []);

  const baseData = useMemo(() => {
    let dataSlice = data;
    if (filter === 'Last 3 Months') dataSlice = data.slice(-3);
    else if (filter === 'Last 6 Months') dataSlice = data.slice(-6);
    else if (filter === 'Year to Date') dataSlice = data.slice(-12);
    // Ignore startDate/endDate as they are mostly visual right now but could be applied
    return dataSlice;
  }, [filter, startDate, endDate]);

  const filteredData = useMemo(() => {
    if (!isCumulative) {
      return baseData.map((item) => {
        const fullIndex = data.findIndex(d => d.month === item.month);
        const prevItem = fullIndex > 0 ? data[fullIndex - 1] : { value: 0, target: 0, benchmark: 0 };
        return {
          ...item,
          value: item.value - (prevItem.value || 0),
          target: item.target - (prevItem.target || 0),
          benchmark: item.benchmark - (prevItem.benchmark || 0)
        };
      });
    }
    
    return baseData;
  }, [baseData, isCumulative]);

  const eventLog = useMemo(() => {
    const events: Array<{
      id: string;
      date: string;
      type: 'anomaly' | 'milestone' | 'manual';
      title: string;
      description: string;
      color: string;
      bg: string;
      border: string;
      icon: React.ReactNode;
    }> = [];

    baseData.forEach((d, i) => {
      if (d.anomaly) {
        events.push({
          id: `${d.month}-anomaly`,
          date: d.month,
          type: 'anomaly',
          title: 'System Anomaly Detected',
          description: d.anomalyReason || 'Unknown Irregularity',
          color: 'text-rose-400',
          bg: 'bg-rose-900/20',
          border: 'border-rose-500/30',
          icon: <AlertCircle className="w-3.5 h-3.5" />
        });
      }

      if (i > 0) {
        const prev = baseData[i - 1];
        const currentMilestone = Math.floor(d.value / 1000000);
        const prevMilestone = Math.floor(prev.value / 1000000);
        
        if (currentMilestone > prevMilestone) {
          events.push({
            id: `${d.month}-milestone-${currentMilestone}`,
            date: d.month,
            type: 'milestone',
            title: `Capital Milestone: £${currentMilestone}M`,
            description: `Vault surpassed the £${currentMilestone},000,000 algorithmic threshold.`,
            color: 'text-cyan-400',
            bg: 'bg-cyan-900/20',
            border: 'border-cyan-500/30',
            icon: <Activity className="w-3.5 h-3.5" />
          });
        }
      }
    });

    return events.reverse();
  }, [baseData]);

  // Combined searchable elements
  const filteredEventLog = useMemo(() => {
    if (!searchQuery.trim()) return eventLog;
    const q = searchQuery.toLowerCase();
    return eventLog.filter(e => 
      e.title.toLowerCase().includes(q) || 
      e.description.toLowerCase().includes(q) || 
      e.date.toLowerCase().includes(q) || 
      e.type.toLowerCase().includes(q)
    );
  }, [eventLog, searchQuery]);

  const filteredNodes = useMemo(() => {
    if (!searchQuery.trim()) return nodes;
    const q = searchQuery.toLowerCase();
    return nodes.filter(n => 
      n.name.toLowerCase().includes(q) || 
      n.id.toLowerCase().includes(q) || 
      n.category.toLowerCase().includes(q) || 
      n.description.toLowerCase().includes(q) ||
      n.status.toLowerCase().includes(q)
    );
  }, [nodes, searchQuery]);

  const growthPercentage = useMemo(() => {
    if (filteredData.length < 2) return 0;
    const firstValue = filteredData[0].value;
    const lastValue = filteredData[filteredData.length - 1].value;
    return (((lastValue - firstValue) / firstValue) * 100);
  }, [filteredData]);

  const totalVolume = useMemo(() => {
    return filteredData.reduce((acc, curr) => acc + (curr.volume || 0), 0);
  }, [filteredData]);

  const volatilityStatus = useMemo(() => {
    if (filteredData.length < 2) return 'Stable';
    const vals = filteredData.map(d => d.value);
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
    const spread = (max - min) / avg;
    
    if (spread > 0.8) return { label: 'High Volatility', color: 'text-rose-400' };
    if (spread > 0.4) return { label: 'Moderate Volatility', color: 'text-amber-400' };
    return { label: 'Stable Trend', color: 'text-emerald-400' };
  }, [filteredData]);

  const anomaliesDetected = useMemo(() => {
    return filteredData.filter(d => d.anomaly).length;
  }, [filteredData]);

  const isPositiveGrowth = growthPercentage >= 0;

  const handleExport = useCallback(() => {
    if (chartRef.current === null) {
      return;
    }
    toPng(chartRef.current, { cacheBust: true, backgroundColor: '#050914' })
      .then((dataUrl) => {
        const link = document.createElement('a');
        link.download = `Nexorax-Vault-Chart-${filter}.png`;
        link.href = dataUrl;
        link.click();
      })
      .catch((err) => {
        console.error('Error exporting chart:', err);
      });
  }, [chartRef, filter]);

  // EXPORT TEMPORAL LEDGER & AUTOMATION NODES AS SECURE PRINTED PDF
  const exportTemporalLedgerAsPDF = useCallback(() => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert("Error: Pop-up blocked! Please allow popups to export the secure temporal ledger logs as PDF.");
      return;
    }
    
    const logsHTML = filteredEventLog.map(ev => `
      <div style="border-bottom: 1px solid #1e293b; padding: 12px 0; margin-bottom: 8px;">
        <div style="display: flex; justify-content: space-between; font-family: monospace; font-size: 11px;">
          <strong style="color: #22d3ee; text-transform: uppercase;">${ev.title}</strong>
          <span style="color: #64748b;">${ev.date}</span>
        </div>
        <p style="color: #cbd5e0; font-size: 11px; margin: 4px 0 0 0; line-height: 1.5;">${ev.description}</p>
        <span style="display: inline-block; background: #0f172a; border: 1px solid #1e293b; font-size: 8px; padding: 1px 5px; border-radius: 3px; margin-top: 5px; color: #38bdf8; font-family: monospace; text-transform: uppercase;">
          CATEGORY: ${ev.type}
        </span>
      </div>
    `).join('');

    const nodesHTML = nodes.map(n => `
      <tr style="border-bottom: 1px solid #1e293b;">
        <td style="padding: 10px; font-family: monospace; font-size: 11px; color: #38bdf8;">${n.id}</td>
        <td style="padding: 10px; font-family: sans-serif; font-size: 11px; font-weight: bold; color: #fff;">${n.name}</td>
        <td style="padding: 10px; font-family: monospace; font-size: 10px; color: #64748b;">${n.category}</td>
        <td style="padding: 10px;"><span style="display: inline-block; padding: 1px 5px; font-family: monospace; font-size: 9px; font-weight: bold; border-radius: 3px; background: ${n.status === 'ACTIVE' ? '#064e3b' : n.status === 'SYNCING' ? '#1e3a8a' : n.status === 'SUSPENDED' ? '#7f1d1d' : '#334155'}; color: ${n.status === 'ACTIVE' ? '#34d399' : n.status === 'SYNCING' ? '#60a5fa' : n.status === 'SUSPENDED' ? '#f87171' : '#cbd5e0'};">${n.status}</span></td>
        <td style="padding: 10px; font-family: monospace; font-size: 10px; color: #94a3b8;">${n.latency}</td>
        <td style="padding: 10px; font-family: sans-serif; font-size: 10.5px; color: #94a3b8; line-height: 1.4;">${n.description}</td>
      </tr>
    `).join('');

    printWindow.document.write(`
      <html>
        <head>
          <title>NexoraX Imperial Vault - Secure Temporal Ledger Report</title>
          <style>
            body {
              background-color: #030712;
              color: #f3f4f6;
              font-family: 'Inter', system-ui, -apple-system, sans-serif;
              padding: 40px;
              margin: 0;
            }
            .header {
              border-bottom: 2px solid #22d3ee;
              padding-bottom: 20px;
              margin-bottom: 25px;
              display: flex;
              justify-content: space-between;
              align-items: flex-end;
            }
            .title {
              font-size: 18px;
              font-weight: 950;
              text-transform: uppercase;
              letter-spacing: 2px;
              color: #22d3ee;
              margin: 0;
            }
            .subtitle {
              font-size: 9px;
              font-family: monospace;
              letter-spacing: 1px;
              color: #64748b;
              text-transform: uppercase;
              margin-top: 4px;
            }
            .metadata {
              text-align: right;
              font-family: monospace;
              font-size: 9.5px;
              color: #94a3b8;
              line-height: 1.5;
            }
            .section-title {
              font-size: 11px;
              font-family: monospace;
              text-transform: uppercase;
              letter-spacing: 1.5px;
              color: #38bdf8;
              border-bottom: 1px solid #1e293b;
              padding-bottom: 6px;
              margin-top: 25px;
              margin-bottom: 15px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 25px;
            }
            th {
              background: #0f172a;
              color: #38bdf8;
              text-transform: uppercase;
              font-family: monospace;
              font-size: 9px;
              padding: 10px;
              text-align: left;
              border-bottom: 1.5px solid #22d3ee;
            }
            .disclaimer {
              border: 1px dashed #ef444430;
              background: #7f1d1d15;
              color: #f87171;
              font-size: 9.5px;
              padding: 12px;
              border-radius: 6px;
              margin-top: 35px;
              font-family: monospace;
              line-height: 1.5;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div>
              <h1 class="title">NEXORAX IMPERIAL VAULT SECURE REPORT</h1>
              <div class="subtitle">Temporal Ledger Logs & Automation Node State Configuration</div>
            </div>
            <div class="metadata">
              <strong>STATUS:</strong> VERIFIED ARCHIVAL COPY<br>
              <strong>COMPANY:</strong> NexoraX Digital Ltd (SC887095)<br>
              <strong>DEPARTMENT:</strong> Office of the Director Lead Director<br>
              <strong>TIMESTAMP:</strong> ${new Date().toLocaleString()}
            </div>
          </div>

          <div class="section-title">I. Active Automation Nodes Configuration (15-Minute Window)</div>
          <table>
            <thead>
              <tr>
                <th style="width: 12%">NODE_ID</th>
                <th style="width: 18%">NAME</th>
                <th style="width: 22%">CATEGORY</th>
                <th style="width: 10%">STATUS</th>
                <th style="width: 8%">LATENCY</th>
                <th style="width: 30%">OPERATIONAL DESCRIPTION</th>
              </tr>
            </thead>
            <tbody>
              ${nodesHTML}
            </tbody>
          </table>

          <div class="section-title">II. Temporal Ledger Logs & Event Records (Filtered Scope)</div>
          <div style="background: #090d16; border: 1px solid #1e293b; padding: 15px; border-radius: 6px;">
            ${logsHTML || '<p style="color: #64748b; font-size: 11px; font-family: monospace; text-align: center; padding: 15px;">No matching event logs loaded in the current scope.</p>'}
          </div>

          <div class="disclaimer">
            <strong>CONFIDENTIALITY DIRECTIVE (RESTRICTED):</strong> This document contains highly proprietary algorithm signatures and temporal transaction metrics belonging strictly to NexoraX Digital Ltd (SC887095). Unauthorized distribution, copying, or replication of this temporal ledger violates active corporate compliance rules. All actions logged with hash signature tags.
          </div>

          <div style="text-align: center; font-size: 8px; font-family: monospace; color: #475569; margin-top: 30px;">
            --- END OF SECURE PDF TRANSMISSION • SECURED BY CLOUDSYNC-02 ---
          </div>

          <script>
            window.onload = function() {
              window.print();
              setTimeout(function(){ window.close(); }, 500);
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
    alert("Temporal Ledger PDF generated cleanly! Opening print dialog...");
  }, [filteredEventLog, nodes]);

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1.0] }}
      className="w-full min-h-[900px] h-auto bg-[#050914]/80 backdrop-blur-sm border border-cyan-900/40 rounded-xl p-6 flex flex-col mb-4 shadow-lg shrink-0 relative group"
    >
      <div ref={chartRef} className="flex flex-col w-full bg-[#050914]">
        <AnimatePresence>
          {!isPositiveGrowth && (
            <motion.div 
              initial={{ opacity: 0, height: 0, marginBottom: 0 }}
              animate={{ opacity: 1, height: 'auto', marginBottom: 16 }}
              exit={{ opacity: 0, height: 0, marginBottom: 0 }}
              className="flex items-center gap-2 bg-rose-900/20 border border-rose-500/30 text-rose-400 p-2 rounded-lg text-[10px] font-mono tracking-widest uppercase overflow-hidden"
            >
              <AlertCircle className="w-3.5 h-3.5 shrink-0" />
              <span>Trend Alert: Negative yield trajectory detected in current window.</span>
            </motion.div>
          )}
          {anomaliesDetected > 0 && isPositiveGrowth && (
            <motion.div 
              initial={{ opacity: 0, height: 0, marginBottom: 0 }}
              animate={{ opacity: 1, height: 'auto', marginBottom: 16 }}
              exit={{ opacity: 0, height: 0, marginBottom: 0 }}
              className="flex items-center gap-2 bg-amber-900/20 border border-amber-500/30 text-amber-400 p-2 rounded-lg text-[10px] font-mono tracking-widest uppercase overflow-hidden"
            >
              <AlertCircle className="w-3.5 h-3.5 shrink-0" />
              <span>System Alert: {anomaliesDetected} {anomaliesDetected === 1 ? 'Anomaly' : 'Anomalies'} detected in selected time range.</span>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* UPPER VAULT HUD BAR WITH SECURE ACTION CONTROLLERS */}
        <div className="mb-4 p-3 bg-[#0a0f1d]/90 border border-cyan-500/10 rounded-xl flex flex-col sm:flex-row justify-between items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="p-1.5 bg-cyan-950/40 rounded border border-cyan-500/20 text-cyan-400">
              <ShieldCheck size={14} className="animate-pulse" />
            </span>
            <div>
              <span className="text-white font-extrabold uppercase text-[10px] block font-mono">
                🛡️ IMPERIAL COGNITIVE CORE SYSTEM VAULT (NODE-17.2)
              </span>
              <span className="text-[8.5px] text-slate-400 uppercase tracking-widest block font-mono">
                VAT LIABILITIES, SPARTAN STRIKES & TEMPORAL CLOSE PIPELINES
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-2 shrink-0">
            {/* VAULT STATE PERSISTENCE TRIGGER */}
            <button
              onClick={handleSaveSnapshot}
              className="px-3 py-1.5 bg-zinc-950 hover:bg-[#0f172a] border border-cyan-500/20 hover:border-cyan-400/50 text-[#00ffff] font-mono font-bold uppercase text-[9px] rounded-lg cursor-pointer transition-all flex items-center gap-1"
              title="Saves configuration of active automation nodes to local storage"
            >
              <Save size={11} /> VAULT SNAPSHOT
            </button>

            {/* LEDGER SECURE PDF EXPORTER */}
            <button
              onClick={exportTemporalLedgerAsPDF}
              className="px-3 py-1.5 bg-gradient-to-r from-purple-950 to-[#22d3ee]/20 hover:from-purple-900 hover:to-[#22d3ee]/40 border border-cyan-400/30 text-white font-mono font-black uppercase text-[9px] rounded-lg cursor-pointer transition-all flex items-center gap-1.5"
              title="Export temporal ledger log details as a secure printed PDF report"
            >
              <FileText size={11} className="text-cyan-400" /> FILE SECURE PDF
            </button>
          </div>
        </div>

        <div className="mb-6 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h3 className="text-cyan-400 font-mono text-xs uppercase tracking-widest mb-1 shadow-[0_0_10px_rgba(34,211,238,0.2)]">Capital Vault (Current Value)</h3>
          <div className="flex items-end gap-3 mt-2">
            <p className="text-4xl lg:text-5xl font-bold text-white font-sans tracking-wide drop-shadow-[0_0_8px_rgba(34,211,238,0.4)]">£{filteredData[filteredData.length - 1]?.value.toLocaleString()}</p>
            <motion.span 
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className={`font-mono text-sm mb-1 px-2 py-0.5 rounded border shadow-[0_0_10px_rgba(52,211,153,0.2)] flex items-center gap-1 ${isPositiveGrowth ? 'text-emerald-400 bg-emerald-900/30 border-emerald-500/30' : 'text-rose-400 bg-rose-900/30 border-rose-500/30 shadow-[0_0_10px_rgba(244,63,94,0.2)]'}`}
            >
              {isPositiveGrowth ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />} 
              {isPositiveGrowth ? '+' : ''}{growthPercentage.toFixed(1)}%
            </motion.span>
          </div>
          
          {/* Metrics Row */}
          <div className="flex items-center gap-4 mt-4 text-[10px] uppercase font-mono border-t border-cyan-900/40 pt-3 w-max">
            <div className="flex items-center gap-1.5 text-slate-400">
              <BarChart2 className="w-3 h-3 text-cyan-500" />
              <span className="text-slate-500">Vol:</span>
              <span className="text-cyan-400">£{(totalVolume / 1000).toFixed(1)}k</span>
            </div>
            <div className="w-[1px] h-3 bg-cyan-900/60"></div>
            <div className="flex items-center gap-1.5 text-slate-400">
              <Activity className="w-3 h-3 text-indigo-400" />
              <span className="text-slate-500">Risk:</span>
              <span className={volatilityStatus.color}>{volatilityStatus.label}</span>
            </div>
          </div>
          
          {/* Interactive Legend */}
          <div className="flex flex-wrap items-center gap-4 mt-4 font-mono text-[10px] uppercase">
            <button 
              onClick={() => setShowActual(!showActual)} 
              className={`flex items-center gap-1.5 transition-opacity hover:opacity-100 ${showActual ? 'opacity-100' : 'opacity-40'}`}
            >
              <span className="w-2 h-2 rounded-full bg-cyan-400 border border-cyan-400 shadow-[0_0_5px_rgba(34,211,238,0.5)]"></span> Actual Yield
            </button>
            <button 
              onClick={() => setShowTarget(!showTarget)} 
              className={`flex items-center gap-1.5 transition-opacity hover:opacity-100 ${showTarget ? 'opacity-100' : 'opacity-40'}`}
            >
              <span className="w-2 h-2 rounded-full bg-indigo-500/20 border border-t-indigo-400 border-dashed"></span> Target Trajectory
            </button>
            <button 
              onClick={() => setShowBenchmark(!showBenchmark)} 
              className={`flex items-center gap-1.5 transition-opacity hover:opacity-100 ${showBenchmark ? 'opacity-100' : 'opacity-40'}`}
            >
              <span className="w-2 h-2 rounded-full bg-amber-500/20 border border-t-amber-500 border-dashed"></span> Benchmark View
            </button>
            <button 
              onClick={() => setIsCumulative(!isCumulative)} 
              className={`flex items-center gap-1.5 transition-all text-slate-300 hover:text-white ${!isCumulative ? 'text-amber-400' : ''}`}
            >
              <Layers className="w-3 h-3" /> {isCumulative ? 'Cumulative' : 'Delta (MoM)'}
            </button>
          </div>
        </div>
        
        <div className="flex flex-col items-start md:items-end gap-3 z-10 w-full md:w-auto">
          {/* Date Range Picker (Visual) */}
          <div className="flex items-center gap-2 bg-[#0b1222]/80 border border-cyan-900/60 p-1.5 rounded-lg backdrop-blur-md w-full md:w-auto justify-between md:justify-start overflow-x-auto">
            <input 
              type="date" 
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-transparent text-cyan-400 outline-none font-mono text-[10px] uppercase tracking-wider cursor-pointer"
            />
            <span className="text-slate-500 font-mono text-[10px]">TO</span>
            <input 
              type="date" 
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="bg-transparent text-cyan-400 outline-none font-mono text-[10px] uppercase tracking-wider cursor-pointer"
            />
          </div>
          
          <div className="flex justify-between md:justify-end gap-2 w-full overflow-x-auto">
            <div className="flex bg-[#0b1222]/80 border border-cyan-900/60 p-1 rounded-lg backdrop-blur-md whitespace-nowrap">
              {(['Last 3 Months', 'Last 6 Months', 'Year to Date', 'All Time'] as FilterOption[]).map((opt) => (
                <button
                  key={opt}
                  onClick={() => setFilter(opt)}
                  className={`px-3 py-1 text-[9px] font-mono uppercase tracking-widest rounded transition-all ${filter === opt ? 'bg-cyan-900/60 text-white shadow-[0_0_10px_rgba(34,211,238,0.2)] border border-cyan-500/50' : 'text-slate-500 hover:text-cyan-400 hover:bg-white/5 border border-transparent'}`}
                >
                  {filterLabels[opt] || opt}
                </button>
              ))}
            </div>
            
            <button 
              onClick={handleExport}
              className="flex items-center gap-2 text-slate-400 hover:text-cyan-300 transition-colors font-mono text-[10px] uppercase bg-black/40 border border-white/5 hover:border-cyan-500/30 px-3 py-1.5 rounded"
            >
              <Download className="w-3 h-3" /> Export
            </button>
          </div>
        </div>
      </div>
      </div>
      <div className="flex-1 w-full relative h-[250px] md:h-[300px]">
        <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
          <AreaChart data={filteredData} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.4}/>
                <stop offset="95%" stopColor="#22d3ee" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorTarget" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#818cf8" stopOpacity={0.15}/>
                <stop offset="95%" stopColor="#818cf8" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#083344" vertical={false} />
            
            {/* Heatmap Overlay for Anomalies */}
            {filteredData.map((d, index) => {
              if (d.anomaly && d.anomalyCount && d.anomalyCount > 0) {
                const prevMonth = filteredData[Math.max(0, index - 1)].month;
                const nextMonth = filteredData[Math.min(filteredData.length - 1, index + 1)].month;
                // Intensity scales with frequency count (0.05 per count, capped at 0.35)
                const opacity = Math.min(0.35, d.anomalyCount * 0.05);
                
                return (
                  <React.Fragment key={`heatmap-${d.month}`}>
                    <ReferenceArea
                      {...({
                        yAxisId: "left",
                        x1: prevMonth,
                        x2: nextMonth,
                        fill: "#f43f5e",
                        fillOpacity: opacity,
                        strokeOpacity: 0,
                        ifOverflow: "hidden"
                      } as any)}
                    />
                  </React.Fragment>
                );
              }
              return null;
            })}

            <XAxis 
              dataKey="month" 
              stroke="#0891b2" 
              fontSize={10} 
              tickMargin={10} 
              axisLine={false} 
              tickLine={false} 
              fontFamily="monospace"
            />
            <YAxis 
               yAxisId="left"
               stroke="#0891b2" 
               fontSize={10} 
               tickFormatter={(value) => `£${(value / 1000000).toFixed(1)}M`}
               axisLine={false} 
               tickLine={false}
               fontFamily="monospace"
            />
            {showBenchmark && (
              <YAxis 
                 yAxisId="right"
                 orientation="right"
                 stroke="#f59e0b" 
                 fontSize={10} 
                 tickFormatter={(value) => `£${(value / 1000000).toFixed(1)}M`}
                 axisLine={false} 
                 tickLine={false}
                 fontFamily="monospace"
              />
            )}
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#0891b2', strokeWidth: 1, strokeDasharray: '4 4' }} />
            
            {/* Target Area (Comparison Overlay) */}
            {showTarget && <Area 
              yAxisId="left"
              type="monotone" 
              dataKey="target" 
              stroke="#6366f1" 
              strokeWidth={1.5} 
              strokeDasharray="4 4" 
              fillOpacity={1} 
              fill="url(#colorTarget)" 
              isAnimationActive={true}
              animationDuration={1500}
              dot={false}
              activeDot={false}
            />}

            {/* Benchmark Area (Comparison Overlay) */}
            {showBenchmark && <Area 
              yAxisId="right"
              type="monotone" 
              dataKey="benchmark" 
              stroke="#f59e0b" 
              strokeWidth={1.5} 
              strokeDasharray="4 4" 
              fillOpacity={0.1} 
              fill="#f59e0b" 
              isAnimationActive={true}
              animationDuration={1500}
              dot={false}
              activeDot={false}
            />}

            {/* Actual Area */}
            {showActual && <Area 
              yAxisId="left"
              type="monotone" 
              dataKey="value" 
              stroke="#22d3ee" 
              strokeWidth={2.5} 
              fillOpacity={1} 
              fill="url(#colorValue)" 
              isAnimationActive={true}
              animationDuration={1500}
              animationEasing="ease-out"
              dot={{ r: 3, fill: '#050914', stroke: '#22d3ee', strokeWidth: 1.5 }}
              activeDot={{ r: 6, fill: "#22d3ee", stroke: "#050914", strokeWidth: 2, shadow: '0 0 10px #22d3ee' }} 
            />}

            {/* Anomaly Highlighting via ReferenceDot */}
            {showActual && filteredData.filter(d => d.anomaly).map((d, index) => (
              <ReferenceDot 
                yAxisId="left"
                key={index} 
                x={d.month} 
                y={d.value} 
                r={5} 
                fill="#f43f5e" 
                stroke="#050914" 
                strokeWidth={2}
                isFront={true}
              />
            ))}

            <Brush 
              dataKey="month" 
              height={20} 
              stroke="#0891b2"
              fill="#0b1222"
              tickFormatter={() => ''}
              travellerWidth={8}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* SEARCH CONTROL BAR FOR PRECISE SEARCH IN AUTOMATION CODES OR EXTRACTION TASKS */}
      <div className="mt-4 p-3 bg-black/60 border border-cyan-500/10 rounded-xl flex flex-col md:flex-row items-center justify-between gap-3 shrink-0">
        <div className="flex items-center gap-2 w-full md:w-auto">
          <span className="text-[#00ffff] font-mono text-[10px] uppercase font-black shrink-0 tracking-wider">
            🔎 VAULT FILTER ENGINE: 
          </span>
          <div className="relative w-full md:w-[280px]">
            <input
              type="text"
              placeholder="Filter nodes, 15m tasks, or logs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-zinc-950 border border-cyan-500/20 hover:border-cyan-400/40 focus:border-[#00ffff] focus:outline-none rounded-lg py-1.5 pl-8 pr-3 text-white font-mono text-[10.5px] uppercase placeholder-slate-600 transition-all"
            />
            <Search size={11} className="absolute left-2.5 top-2.5 text-slate-500" />
          </div>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="px-2 py-1 text-slate-500 hover:text-white uppercase font-mono text-[9px] shrink-0"
            >
              [ Clear ]
            </button>
          )}
        </div>
        <div className="text-[9.5px] text-slate-500 font-mono uppercase tracking-widest hidden md:block">
          Active extraction tasks mapping & temporal logging sync (CloudSync-02)
        </div>
      </div>

      {/* LOWER PANEL: TWO-COLUMN OBSIDIAN HUD GRID FOR EVENTS AND AUTOMATION NODES */}
      <div className="mt-4 grid grid-cols-1 lg:grid-cols-12 gap-6 pt-2">
        
        {/* Left Column (Automation Nodes list/grid) - 7 sectors */}
        <div className="lg:col-span-7 bg-[#050914] border border-cyan-900/40 rounded-xl p-4 flex flex-col space-y-3">
          <div className="flex justify-between items-center border-b border-cyan-900/40 pb-2">
            <h4 className="text-cyan-400 font-mono text-[10.5px] uppercase tracking-widest flex items-center gap-2">
              <Cpu className="w-3.5 h-3.5" /> 15-Minute Window Active Automation Nodes & Extraction Tasks
            </h4>
            <span className="text-[10px] text-slate-500 font-mono">
              ({filteredNodes.length} Sectors Match)
            </span>
          </div>

          <div className="space-y-2 max-h-[290px] overflow-y-auto pr-1 select-none custom-scrollbar">
            {filteredNodes.length > 0 ? (
              filteredNodes.map((n) => {
                const statusColors = {
                  ACTIVE: 'text-emerald-400 border-emerald-500/30 bg-emerald-900/20',
                  SYNCING: 'text-indigo-400 border-indigo-500/30 bg-indigo-900/20',
                  STANDBY: 'text-slate-400 border-slate-700/40 bg-zinc-900/20',
                  SUSPENDED: 'text-rose-450 border-rose-500/30 bg-rose-955/25'
                };
                return (
                  <div 
                    key={n.id}
                    className="p-3 border border-zinc-902 hover:border-cyan-500/20 rounded-lg bg-zinc-950/40 hover:bg-zinc-950/90 transition-all space-y-2 text-left"
                  >
                    <div className="flex justify-between items-start gap-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-cyan-400 font-mono font-bold bg-cyan-950/30 border border-cyan-500/20 px-1.5 py-0.5 rounded">
                            {n.id}
                          </span>
                          <span className="text-white font-sans font-bold text-xs">
                            {n.name}
                          </span>
                        </div>
                        <span className="text-[9px] text-slate-400 uppercase font-mono tracking-wider block mt-1">
                          {n.category} • Latency: <span className="text-emerald-400 font-bold">{n.latency}</span>
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => handleToggleNodeStatus(n.id)}
                          className={`text-[8px] font-black font-mono tracking-wider uppercase px-2 py-1 rounded border ${statusColors[n.status as keyof typeof statusColors] || statusColors.STANDBY} hover:scale-105 active:scale-95 transition-all cursor-pointer`}
                          title="Toggle Status"
                        >
                          {n.status}
                        </button>
                      </div>
                    </div>

                    <p className="text-slate-450 text-[10.5px] leading-relaxed font-sans">
                      {n.description}
                    </p>
                    <div className="flex justify-between items-center text-[7.5px] text-slate-600 uppercase font-mono pt-1 border-t border-white/5">
                      <span>Last triggered: {n.lastExecution}</span>
                      <span>Security: Vetted Node 100%</span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="flex flex-col items-center justify-center py-10 border border-slate-800/40 border-dashed rounded-lg bg-slate-900/10">
                <span className="text-slate-500 font-mono text-[10px] uppercase">
                  No active automation nodes found for criteria: "{searchQuery}"
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Right Column (Temporal Ledger Event Logs) - 5 sectors */}
        <div className="lg:col-span-5 bg-[#050914] border border-cyan-900/40 rounded-xl p-4 flex flex-col space-y-3">
          <div className="flex justify-between items-center border-b border-cyan-900/40 pb-2">
            <h4 className="text-cyan-400 font-mono text-[10.5px] uppercase tracking-widest flex items-center gap-2">
              <Activity className="w-3.5 h-3.5" /> Temporal Ledger Logs & Event milestones
            </h4>
            <span className="text-[10px] text-slate-500 font-mono">
              ({filteredEventLog.length} Logs Match)
            </span>
          </div>

          <div className="space-y-2 max-h-[290px] overflow-y-auto pr-1 custom-scrollbar">
            {filteredEventLog.length > 0 ? (
              filteredEventLog.map((ev) => (
                <motion.div 
                  key={ev.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  className={`flex gap-3 items-start p-2.5 rounded-lg border bg-gradient-to-r from-transparent to-${ev.bg.split('-')[1]}-900/10 ${ev.bg} ${ev.border} text-left`}
                >
                  <div className={`mt-0.5 shrink-0 ${ev.color}`}>
                    {ev.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start gap-3">
                      <h5 className={`font-mono text-xs uppercase tracking-wide truncate ${ev.color}`}>{ev.title}</h5>
                      <span className="text-slate-500 font-mono text-[9px] uppercase whitespace-nowrap">{ev.date}</span>
                    </div>
                    <p className="text-slate-400 text-[10.5px] mt-1 leading-relaxed">
                      {ev.description}
                    </p>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-10 border border-slate-800/40 border-dashed rounded-lg bg-slate-900/10">
                <span className="text-slate-500 font-mono text-[10px] uppercase">
                  No temporal log files matched criteria.
                </span>
              </div>
            )}
          </div>
        </div>

      </div>
    </motion.div>
  );
};
